import * as React from 'react';
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import AlignHorizontalLeft from '@mui/icons-material/AlignHorizontalLeft';
import AlignHorizontalCenter from '@mui/icons-material/AlignHorizontalCenter';
import AlignHorizontalRight from '@mui/icons-material/AlignHorizontalRight';
import VerticalAlignTop from '@mui/icons-material/VerticalAlignTop';
import VerticalAlignCenter from '@mui/icons-material/VerticalAlignCenter';
import VerticalAlignBottom from '@mui/icons-material/VerticalAlignBottom';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup, {
    toggleButtonGroupClasses,
} from '@mui/material/ToggleButtonGroup';
import { checkBit } from '../utils/util';
import Constants from '../config/constants';

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    [`& .${toggleButtonGroupClasses.grouped}`]: {
        border: 0,
        borderRadius: theme.shape.borderRadius,
        [`&.${toggleButtonGroupClasses.disabled}`]: {
            border: 0,
        },
    }
}));

export default function ImageStyleToggleButtonGroup({ style, onImageStyleUpdate }) {
    const [alignment, setAlignment] = useState(Constants.STYLE_ALIGN_CENTER);
    const [verticalAlignment, setVerticalAlignment] = useState(Constants.STYLE_VERTICAL_ALIGN_CENTER);


    useEffect(() => bootstrapStyle(style));

    const handleAlignment = (event, newAlignment) => {
        if (newAlignment == null) {
            return;
        }
        setAlignment(newAlignment);
        onImageStyleUpdate(verticalAlignment | newAlignment);
    };

    const handleVerticalAlignment = (event, newVerticalAlignment) => {
        if (newVerticalAlignment == null) {
            return;
        }
        setVerticalAlignment(newVerticalAlignment);
        onImageStyleUpdate(alignment | newVerticalAlignment);
    };


    const bootstrapStyleAlignment = (style) => {
        if (checkBit(style, Constants.STYLE_ALIGN_CENTER)) {
            setAlignment(Constants.STYLE_ALIGN_CENTER);
            return;
        }

        if (checkBit(style, Constants.STYLE_ALIGN_LEFT)) {
            setAlignment(Constants.STYLE_ALIGN_LEFT);
            return;
        }

        if (checkBit(style, Constants.STYLE_ALIGN_RIGHT)) {
            setAlignment(Constants.STYLE_ALIGN_RIGHT);
            return;
        }
    };

    const bootstrapStyleVerticalAlignment = (style) => {
        if (checkBit(style, Constants.STYLE_VERTICAL_ALIGN_CENTER)) {
            setVerticalAlignment(Constants.STYLE_VERTICAL_ALIGN_CENTER);
            return;
        }

        if (checkBit(style, Constants.STYLE_VERTICAL_ALIGN_TOP)) {
            setVerticalAlignment(Constants.STYLE_VERTICAL_ALIGN_TOP);
            return;
        }

        if (checkBit(style, Constants.STYLE_VERTICAL_ALIGN_BOTTOM)) {
            setVerticalAlignment(Constants.STYLE_VERTICAL_ALIGN_BOTTOM);
            return;
        }
    };

    const bootstrapStyle = (style) => {
        bootstrapStyleAlignment(style);
        bootstrapStyleVerticalAlignment(style);
    };

    return (
        <div>
            <Paper
                elevation={0}
                sx={(theme) => ({
                    display: 'flex',
                    border: `1px solid ${theme.palette.divider}`,
                    flexWrap: 'wrap',
                })}>
                <StyledToggleButtonGroup
                    size="large"
                    exclusive
                    value={alignment}
                    onChange={handleAlignment}
                    aria-label="text alignment">
                    <ToggleButton value={Constants.STYLE_ALIGN_LEFT} aria-label="left aligned">
                        <AlignHorizontalLeft />
                    </ToggleButton>
                    <ToggleButton value={Constants.STYLE_ALIGN_CENTER} aria-label="centered">
                        <AlignHorizontalCenter />
                    </ToggleButton>
                    <ToggleButton value={Constants.STYLE_ALIGN_RIGHT} aria-label="right aligned">
                        <AlignHorizontalRight />
                    </ToggleButton>
                </StyledToggleButtonGroup>
                <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1 }} />
                <StyledToggleButtonGroup
                    size="large"
                    exclusive
                    value={verticalAlignment}
                    onChange={handleVerticalAlignment}
                    aria-label="text alignment">
                    <ToggleButton value={Constants.STYLE_VERTICAL_ALIGN_BOTTOM} aria-label="left aligned">
                        <VerticalAlignBottom />
                    </ToggleButton>
                    <ToggleButton value={Constants.STYLE_VERTICAL_ALIGN_CENTER} aria-label="centered">
                        <VerticalAlignCenter />
                    </ToggleButton>
                    <ToggleButton value={Constants.STYLE_VERTICAL_ALIGN_TOP} aria-label="right aligned">
                        <VerticalAlignTop />
                    </ToggleButton>
                </StyledToggleButtonGroup>
            </Paper>
        </div>
    );
}

ImageStyleToggleButtonGroup.propTypes = {
    style: PropTypes.number,
    onImageStyleUpdate: PropTypes.func.isRequired,
};